import { useAppState } from "../store/contextProvider";
import { AppActions } from "../store/reducers";
import useCookie from "./useCookie";
import { useLogout } from "./useLogout";
import { Config } from "../../config/config";

export const useDataFetch = () => {
  const { updateStore, config, theme } = useAppState();
  const { getToken } = useCookie();
  const { logout } = useLogout();

  async function callLoginAPI(apiUrl, payload) {
    const apiBaseUrl = config.env?.ORIGINAL_API_BASE_URL || config.env?.API_BASE_URL;
    const url = `${apiBaseUrl}${Config.ITradeAPI.baseUrl}/${apiUrl}`;
    return callAPI(url, payload, { "Content-Type": "application/json" }, "POST", true);
  }

  async function callAPI(apiUrl, payload = {}, headers = {}, method = "POST", isAngel = false, apiBaseUrlOverride = null) {
    let apiResponse = { status: false, data: null, error: null };
    let sessionExpired = false;
    const pageState = { isLoading: false, pageError: null };

    updateStore({
      type: AppActions.updatePageState,
      payload: { ...pageState, isLoading: true, pageError: null },
    });

    try {
      if (!isAngel) {
        // The session itself is the HttpOnly cookie the API set at login (sent via credentials below).
        const { profileID } = getToken() || {};
        headers["Content-Type"] = "application/json";
        headers["virtual"] = theme?.isVirtual;
        headers["profileid"] = profileID;
        const apiBaseUrl = apiBaseUrlOverride || config.env?.API_BASE_URL;
        apiUrl = typeof apiUrl === "object" && apiUrl?.url
          ? `${apiBaseUrl}${Config.ITradeAPI.baseUrl}/${apiUrl.url}`
          : `${apiBaseUrl}${Config.ITradeAPI.baseUrl}/${apiUrl}`;
      }

      const request = { method, headers, credentials: "include" };
      if (method === "POST") request.body = JSON.stringify(payload);

      const response = await fetch(apiUrl, request);
      const contentType = response.headers.get("content-type");
      let responseData;

      try {
        if (contentType && contentType.includes("application/json")) {
          responseData = await response.json();
        } else {
          await response.text();
          throw new Error(`Server returned non-JSON: ${response.status} ${response.statusText}`);
        }
      } catch (parseError) {
        apiResponse.error = response.status === 401 || response.status === 403
          ? "Access Denied. Please login again."
          : response.status >= 500
            ? "Server error. Please try again later."
            : parseError.message || "Invalid response from server.";
        responseData = null;
      }

      if (!isAngel && (response.status === 401 || response.status === 403) && responseData?.isValidSession === false) {
        // Session invalid/expired - run the full logout flow (clears everything), then login.
        sessionExpired = true;
        logout();
      }

      if (responseData) {
        const hasError = responseData.errorCode || responseData.errorMessage;
        if (!response.ok || hasError) {
          apiResponse.error = responseData.errorMessage || responseData.error || responseData.message || "Request failed";
        } else {
          apiResponse.status = true;
          apiResponse.data = responseData.data ?? responseData;
        }
      }
    } catch (error) {
      apiResponse.error = error.message || "An unexpected error occurred.";
    } finally {
      const error = apiResponse.error;
      const alert = {};
      if (error && !sessionExpired) {
        alert.showAlert = true;
        alert.alertMessage = error;
        alert.alertType = "error";
      }
      updateStore({
        type: AppActions.updatePageState,
        payload: { ...pageState, isLoading: false, pageError: error, ...alert },
      });
      return apiResponse;
    }
  }

  return { callLoginAPI, callAPI };
};
